import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useDelete, useNotify, useRefresh, useRedirect } from 'react-admin';

interface DeleteWithConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  record: any;
  resource: string;
  nameField: string; // Campo que contiene el nombre a confirmar
  displayName?: string; // Nombre a mostrar en el diálogo
  redirectTo?: string; // Ruta a la que redirigir después de eliminar
}

const DeleteWithConfirmationDialog: React.FC<
  DeleteWithConfirmationDialogProps
> = ({ open, onClose, record, resource, nameField, displayName, redirectTo }) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const nameToConfirm = record?.[nameField] || '';
  const itemDisplayName = displayName || nameToConfirm;

  const handleDelete = async () => {
    if (confirmationText !== nameToConfirm) {
      notify(
        'El nombre no coincide. Por favor, verifica e intenta nuevamente.',
        {
          type: 'warning',
        }
      );
      return;
    }

    setIsDeleting(true);
    try {
      await deleteOne(resource, { id: record.id });
      notify('Elemento eliminado correctamente', { type: 'success' });
      onClose();
      setConfirmationText('');
      if (redirectTo) {
        redirect(redirectTo);
      } else {
        refresh();
      }
    } catch (error) {
      notify('Error al eliminar el elemento', { type: 'error' });
      console.error('Error deleting:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = (event?: any, reason?: string) => {
    if (!isDeleting) {
      setConfirmationText('');
      onClose();
    }
  };

  const handleCancelClick = (event: React.MouseEvent) => {
    handleClose(event);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    handleDelete();
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    handleClose(event);
  };

  const isConfirmationValid = confirmationText === nameToConfirm;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      BackdropProps={{
        onClick: handleBackdropClick,
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <WarningIcon color='error' />
          <Typography variant='h6' component='span'>
            Confirmar Eliminación
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity='warning' sx={{ mb: 3 }}>
          <Typography variant='body2'>
            <strong>¡Atención!</strong> Esta acción no se puede deshacer.
          </Typography>
        </Alert>

        <Typography variant='body1' sx={{ mb: 2 }}>
          Estás a punto de eliminar el siguiente elemento:
        </Typography>

        <Box
          sx={{
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            mb: 3,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            {resource === 'variables'
              ? 'Variable:'
              : resource === 'inputs'
                ? 'Input:'
                : 'Dispositivo:'}
          </Typography>
          <Typography variant='h6' color='primary'>
            {itemDisplayName}
          </Typography>
        </Box>

        <Typography variant='body2' sx={{ mb: 2 }}>
          Para confirmar la eliminación, escribe exactamente el nombre:{' '}
          <strong>{nameToConfirm}</strong>
        </Typography>

        <TextField
          fullWidth
          variant='outlined'
          label='Confirma el nombre'
          value={confirmationText}
          onChange={e => setConfirmationText(e.target.value)}
          placeholder={`Escribe: ${nameToConfirm}`}
          error={confirmationText !== '' && !isConfirmationValid}
          helperText={
            confirmationText !== '' && !isConfirmationValid
              ? 'El nombre no coincide'
              : ''
          }
          disabled={isDeleting}
          autoFocus
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleCancelClick}
          disabled={isDeleting}
          color='inherit'
        >
          Cancelar
        </Button>
        <Button
          onClick={handleDeleteClick}
          disabled={!isConfirmationValid || isDeleting}
          variant='contained'
          color='error'
          sx={{
            minWidth: 120,
          }}
        >
          {isDeleting ? 'Eliminando...' : 'Eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteWithConfirmationDialog;
